import React, { useState, useMemo, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, PieChart, Pie, LabelList
} from 'recharts';
import { db } from './firebase';
import { collection, query, orderBy, getDocs } from './firestore-compat';
import { useBusinessHoursPolling, formatLastUpdated } from './hooks/useBusinessHoursPolling';

// ── Helpers ────────────────────────────────────────────────────────────────────
function toSeconds(val) {
  if (!val || val === '' || val === '0') return 0;
  if (typeof val === 'number') return Math.round(val);
  const s = String(val).trim();
  const parts = s.split(':');
  if (parts.length === 3) return +parts[0] * 3600 + +parts[1] * 60 + +parts[2];
  if (parts.length === 2) return +parts[0] * 60 + +parts[1];
  return parseInt(s) || 0;
}

function secsToHMS(s) {
  if (!s || s === 0) return '0s';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2,'0')}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2,'0')}s`;
  return `${sec}s`;
}

function parseDateTime(s) {
  if (!s) return null;
  const str = String(s).trim();
  const m1 = str.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})/);
  if (m1) return new Date(+m1[3], +m1[2]-1, +m1[1], +m1[4], +m1[5]);
  const m2 = str.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
  if (m2) return new Date(+m2[1], +m2[2]-1, +m2[3], +m2[4], +m2[5]);
  const d = new Date(str);
  return isNaN(d) ? null : d;
}

function detectField(keys, keywords) {
  return keys.find(k =>
    keywords.some(kw => k.toLowerCase().replace(/[\s_-]/g,'').includes(kw.toLowerCase().replace(/[\s_-]/g,'')))
  ) || null;
}

function classifyOrigem(num) {
  if (!num) return 'Indeterminado';
  const digits = String(num).replace(/\D/g, '');
  if (!digits || digits.length < 8) return 'Indeterminado';
  if (digits.length <= 9) return digits.startsWith('9') ? 'Celular Local' : 'Fixo Local';
  const semDDD = digits.slice(2);
  return semDDD.length >= 9 ? 'Celular DDD' : 'Fixo DDD';
}

function getTurno(h) {
  if (h >= 7 && h < 19) return 'Diurno';
  return 'Noturno';
}

const DIAS_SEMANA = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

const FAIXAS_ESPERA = [
  { label: '0s (imediato)',   min: 0,   max: 0   },
  { label: '1s – 30s',        min: 1,   max: 30  },
  { label: '31s – 60s',       min: 31,  max: 60  },
  { label: '61s – 120s',      min: 61,  max: 120 },
  { label: '121s – 150s',     min: 121, max: 150 },
  { label: '151s – 300s',     min: 151, max: 300 },
  { label: 'Acima de 300s',   min: 301, max: Infinity },
];

const FILA_UNIDADE_MAP = {
  'Admissao_Homebaby': 'Homecare',
  'Admissao_Vitalcare': 'Homecare',
  'Atendimento': 'Prontobaby',
  'CAF_CPL': 'CPL',
  'CAF_Homebaby': 'Homecare',
  'CAF_PB_Tijuca': 'Prontobaby',
  'CCIH_PB_Tijuca': 'Prontobaby',
  'Chekout_Pb_Tijuca': 'Prontobaby',
  'Emergencias_Urgencias_Home': 'Homecare',
  'Emergencias_Urgencias_Homecare': 'Homecare',
  'Enfermagem': 'Homecare',
  'Engenharia_Clinica': 'Prontobaby',
  'Equipe_Fisio': 'Homecare',
  'Equipe_Fono': 'Homecare',
  'Equipe_Medica': 'Homecare',
  'Equipe_Nutri': 'Homecare',
  'Escalista': 'Homecare',
  'Exames_Cirurgicos_PB_Tijuca': 'Prontobaby',
  'Exames_Imagem_Barra': 'Barra',
  'Exames_Imagem_CPL': 'CPL',
  'Exames_Imagem_PB_Tijuca': 'Prontobaby',
  'Exames_Laboratoriais_Barra': 'Barra',
  'Exames_Laboratoriais_PB_Tijuca': 'Prontobaby',
  'EXAMES_LABORATORIO_CPL': 'CPL',
  'Exames_remocao_Homebaby': 'Homecare',
  'Exames_remocao_Vitalcare': 'Homecare',
  'Farmacia_Barra': 'Barra',
  'Homebaby_Intercorrencias': 'Homecare',
  'Homecare_Ouvidoria': 'Homecare',
  'Hotelaria_Barra': 'Barra',
  'Hotelaria_CPL': 'CPL',
  'Hotelaria_PB_Tijuca': 'Prontobaby',
  'IEP_Central_Marcacao': 'IEP',
  'IEP_Confirmacao': 'IEP',
  'IEP_Recepcao': 'IEP',
  'Internacao_Barra': 'Barra',
  'Internacao_CPL': 'CPL',
  'Internacao_PB_Tijuca': 'Prontobaby',
  'Marcacao_Cirurgia': 'Prontobaby',
  'Marcacao_Cirurgica_Barra': 'Barra',
  'Marcacao_Cirurgica_CPL': 'CPL',
  'Marcacao_Cirurgica_PB_Tijuca': 'Prontobaby',
  'NUCLEO_ADMINISTRATIVO': 'Homecare',
  'Nutricao_Barra': 'Barra',
  'Nutricao_CPL': 'CPL',
  'Nutricao_PB_Tijuca': 'Prontobaby',
  'Ouvidoria_Grupo_PB': 'Prontobaby',
  'Ouvidoria_Homebaby': 'Homecare',
  'Qualidade_GP': 'Prontobaby',
  'Recepcao_Barra': 'Barra',
  'Recepcao_Tijuca': 'Prontobaby',
  'Recep_Emergencia_CPL': 'CPL',
  'Recep_Emer_Noturna_PB_Tijuca': 'Prontobaby',
  'Remocao': 'Prontobaby',
  'Solicitacoes_equip_Homebaby': 'Homecare',
  'Solicitacoes_equip_Vitalcare': 'Homecare',
  'Solicitacoes_Homebaby': 'Homecare',
  'Solicitacoes_Vitalcare': 'Homecare',
  'Solicitacoes_Mat_Med_Homebaby': 'Homecare',
  'Solicitacoes_Mat_Med_Vitalcare': 'Homecare',
  'Telefonista': 'Prontobaby'
};

function getUnidade(fila) {
  if (!fila) return 'Indeterminado';
  if (FILA_UNIDADE_MAP[fila]) return FILA_UNIDADE_MAP[fila];
  const key = Object.keys(FILA_UNIDADE_MAP).find(k => fila.startsWith(k) || k.startsWith(fila));
  if (key) return FILA_UNIDADE_MAP[key];
  return 'Outros';
}

// ── Paleta ─────────────────────────────────────────────────────────────────────
const C = {
  bg:     '#0f172a',
  card:   'rgba(255,255,255,0.04)',
  border: 'rgba(255,255,255,0.08)',
  text:   '#f1f5f9',
  muted:  '#94a3b8',
  dim:    '#64748b',
  green:  '#10b981',
  red:    '#ef4444',
  blue:   '#6366f1',
  purple: '#8b5cf6',
  orange: '#f59e0b',
  teal:   '#06b6d4',
  yellow: '#eab308',
};

const STATUS_COLORS = {
  'Atendida':    C.green,
  'Abandonada':  C.red,
  'Transferida': C.teal,
};

const CHART_CC = [C.blue, C.green, C.orange, C.red, C.purple, C.teal, C.yellow, '#ec4899'];

// ── Estilos ────────────────────────────────────────────────────────────────────
const S = {
  page: { minHeight: '100vh', background: `linear-gradient(135deg,${C.bg} 0%,#1e293b 50%,${C.bg} 100%)`, padding: '28px 24px', fontFamily: "'Inter','Segoe UI',sans-serif", color: C.text },
  card: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '20px 22px', backdropFilter: 'blur(10px)' },
  secTitle: { fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.blue, marginBottom: 16 },
  th: { padding: '9px 12px', textAlign: 'left', borderBottom: `1px solid ${C.border}`, color: C.muted, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap', background: 'rgba(255,255,255,0.02)' },
  td: { padding: '8px 12px', borderBottom: `1px solid rgba(255,255,255,0.03)`, color: '#cbd5e1', fontSize: '0.82rem' },
  sel: { padding: '7px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.07)', border: `1px solid ${C.border}`, color: C.text, fontSize: '0.82rem', cursor: 'pointer', outline: 'none' },
  chip: (active, color) => ({ padding: '5px 12px', borderRadius: 20, border: `1px solid ${active ? color : C.border}`, background: active ? `${color}20` : 'rgba(255,255,255,0.04)', color: active ? color : C.muted, fontSize: '0.78rem', fontWeight: active ? 700 : 400, cursor: 'pointer', transition: 'all 0.15s' }),
};

// ── Sub-componentes ─────────────────────────────────────────────────────────────
const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', fontSize: '0.82rem', color: '#e2e8f0' }}>
      {label && <div style={{ color: C.muted, marginBottom: 4, fontWeight: 700 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || C.blue }}>
          {p.name}: <strong>{typeof p.value === 'number' ? p.value.toLocaleString('pt-BR') : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

function KpiCard({ icon, label, value, sub, color, warn }) {
  return (
    <div style={{ ...S.card, borderLeft: `4px solid ${color}`, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', right: -10, top: -10, fontSize: '3.5rem', opacity: 0.06 }}>{icon}</div>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: '1.8rem', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.73rem', color: warn ? C.red : C.dim, marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

function SectionTitle({ children, color = C.blue }) {
  return <div style={{ ...S.secTitle, color }}>{children}</div>;
}

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || C.muted;
  return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, background: `${color}20`, color, border: `1px solid ${color}40` }}>{status}</span>;
}

// ── Sub-tela: Análise Mês a Mês ──────────────────────────────────────────────────
// Comparativo mensal de cada indicador que já existe no dashboard (KPIs da Linha 1),
// reorganizado por competência. Não altera nada das abas "Visão Geral" / "Visão por
// Unidade" — é uma seção totalmente separada, renderizada só quando essa aba está ativa.
function MonthlyComparisonSection({ porMesAno, porMesFila }) {
  if (porMesAno.length === 0) {
    return (
      <div style={{ ...S.card, textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: '3rem', marginBottom: 14 }}>📅</div>
        <div style={{ fontWeight: 700, color: C.muted, marginBottom: 8 }}>Sem dados suficientes pra comparar meses</div>
        <div style={{ color: C.dim, fontSize: '0.85rem' }}>Amplie o período no filtro "De / Até" acima (ex.: clique em "Tudo") pra ver mais de um mês.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {porMesAno.length < 2 && (
        <div style={{ ...S.card, borderLeft: `4px solid ${C.orange}`, fontSize: '0.85rem', color: C.muted }}>
          ⚠️ Só há dados de <strong style={{ color: C.text }}>{porMesAno[0].mesLabel}</strong> no período filtrado. Amplie o período (filtro "De / Até" ou o atalho "Tudo") pra comparar com outros meses.
        </div>
      )}

      {/* Volume de chamadas por mês */}
      <div style={S.card}>
        <SectionTitle>📞 Volume de Chamadas por Mês</SectionTitle>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={porMesAno} barCategoryGap="25%" barGap={2} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="mesLabel" tick={{ fill: C.dim, fontSize: 11 }} />
            <YAxis tick={{ fill: C.dim, fontSize: 10 }} />
            <Tooltip content={<Tip />} />
            <Legend wrapperStyle={{ fontSize: '0.78rem', color: C.muted }} />
            <Bar dataKey="total"       name="Total Recebidas" fill={C.blue}  radius={[4,4,0,0]} maxBarSize={44}>
              <LabelList dataKey="total" position="top" fill={C.muted} fontSize={10} />
            </Bar>
            <Bar dataKey="atendidas"   name="Atendidas"       fill={C.green} radius={[4,4,0,0]} maxBarSize={44}>
              <LabelList dataKey="atendidas" position="top" fill={C.muted} fontSize={10} />
            </Bar>
            <Bar dataKey="abandonadas" name="Abandonadas"     fill={C.red}   radius={[4,4,0,0]} maxBarSize={44}>
              <LabelList dataKey="abandonadas" position="top" fill={C.muted} fontSize={10} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Taxas de atendimento/abandono por mês */}
        <div style={S.card}>
          <SectionTitle color={C.green}>✅ Taxa de Atendimento x Abandono por Mês</SectionTitle>
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={porMesAno} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="mesLabel" tick={{ fill: C.dim, fontSize: 11 }} />
              <YAxis tick={{ fill: C.dim, fontSize: 10 }} unit="%" />
              <Tooltip content={<Tip />} formatter={(v) => `${v}%`} />
              <Legend wrapperStyle={{ fontSize: '0.78rem', color: C.muted }} />
              <Line type="monotone" dataKey="taxaAtend"    name="Taxa Atendimento" stroke={C.green} strokeWidth={2.5} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="taxaAbandono" name="Taxa Abandono"    stroke={C.red}   strokeWidth={2.5} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Média de ligações por dia, por mês */}
        <div style={S.card}>
          <SectionTitle color={C.blue}>📈 Média de Ligações por Dia (por Mês)</SectionTitle>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={porMesAno} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="mesLabel" tick={{ fill: C.dim, fontSize: 11 }} />
              <YAxis tick={{ fill: C.dim, fontSize: 10 }} />
              <Tooltip content={<Tip />} />
              <Bar dataKey="mediaLigacoesDia" name="Méd. Ligações/Dia" fill={C.blue} radius={[4,4,0,0]} maxBarSize={44}>
                <LabelList dataKey="mediaLigacoesDia" position="top" fill={C.muted} fontSize={10} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Tempos médios por mês */}
        <div style={S.card}>
          <SectionTitle color={C.orange}>⏱️ Tempo Médio de Espera x Falado por Mês</SectionTitle>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={porMesAno} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="mesLabel" tick={{ fill: C.dim, fontSize: 11 }} />
              <YAxis tick={{ fill: C.dim, fontSize: 10 }} tickFormatter={secsToHMS} />
              <Tooltip content={<Tip />} formatter={(v) => secsToHMS(v)} />
              <Legend wrapperStyle={{ fontSize: '0.78rem', color: C.muted }} />
              <Bar dataKey="mediaEspera" name="Méd. Espera" fill={C.orange} radius={[4,4,0,0]} maxBarSize={44}>
                <LabelList dataKey="mediaEspera" position="top" fill={C.muted} fontSize={10} formatter={secsToHMS} />
              </Bar>
              <Bar dataKey="mediaFalada" name="Méd. Falado"  fill={C.teal}   radius={[4,4,0,0]} maxBarSize={44}>
                <LabelList dataKey="mediaFalada" position="top" fill={C.muted} fontSize={10} formatter={secsToHMS} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Maiores durações por mês */}
        <div style={S.card}>
          <SectionTitle color={C.purple}>📏 Maior Duração em Linha x Maior Tempo Falado</SectionTitle>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={porMesAno} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="mesLabel" tick={{ fill: C.dim, fontSize: 11 }} />
              <YAxis tick={{ fill: C.dim, fontSize: 10 }} tickFormatter={secsToHMS} />
              <Tooltip content={<Tip />} formatter={(v) => secsToHMS(v)} />
              <Legend wrapperStyle={{ fontSize: '0.78rem', color: C.muted }} />
              <Bar dataKey="maiorTotal"  name="Maior Duração" fill={C.purple} radius={[4,4,0,0]} maxBarSize={44}>
                <LabelList dataKey="maiorTotal" position="top" fill={C.muted} fontSize={10} formatter={secsToHMS} />
              </Bar>
              <Bar dataKey="maiorFalada" name="Maior Falado"  fill={'#a78bfa'} radius={[4,4,0,0]} maxBarSize={44}>
                <LabelList dataKey="maiorFalada" position="top" fill={C.muted} fontSize={10} formatter={secsToHMS} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Quantitativo de atendimentos por fila, mês a mês */}
      {porMesFila.linhas.length > 0 && (
        <div style={S.card}>
          <SectionTitle color={C.teal}>🏢 Atendimentos por Fila / Setor, Mês a Mês</SectionTitle>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={S.th}>Fila / Setor</th>
                  {porMesFila.meses.map(m => (
                    <th key={m.mesKey} style={{ ...S.th, textAlign: 'right' }}>{m.mesLabel}</th>
                  ))}
                  <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {porMesFila.linhas.map((row, i) => (
                  <tr key={row.fila} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                    <td style={{ ...S.td, fontWeight: 700, color: C.text }}>{row.fila}</td>
                    {porMesFila.meses.map(m => (
                      <td key={m.mesKey} style={{ ...S.td, textAlign: 'right' }}>{(row[m.mesKey] || 0).toLocaleString('pt-BR')}</td>
                    ))}
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 800, color: C.blue }}>{row.total.toLocaleString('pt-BR')}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: `2px solid rgba(255,255,255,0.08)` }}>
                  <td style={{ ...S.td, fontWeight: 800, color: C.text }}>Total</td>
                  {porMesFila.meses.map(m => (
                    <td key={m.mesKey} style={{ ...S.td, textAlign: 'right', fontWeight: 800 }}>
                      {porMesFila.linhas.reduce((sum, row) => sum + (row[m.mesKey] || 0), 0).toLocaleString('pt-BR')}
                    </td>
                  ))}
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 800, color: C.blue }}>
                    {porMesFila.linhas.reduce((sum, row) => sum + row.total, 0).toLocaleString('pt-BR')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tabela-resumo com todos os indicadores lado a lado */}
      <div style={S.card}>
        <SectionTitle color={C.yellow}>📋 Resumo Comparativo — Todos os Indicadores</SectionTitle>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={S.th}>Mês</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Atendidas</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Abandonadas</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Taxa Atend.</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Taxa Aband.</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Méd. Espera</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Méd. Falado</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Maior Duração</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Maior Falado</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Méd. Lig./Dia</th>
              </tr>
            </thead>
            <tbody>
              {porMesAno.map((m, i) => (
                <tr key={m.mesKey} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                  <td style={{ ...S.td, fontWeight: 700, color: C.text }}>{m.mesLabel}</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 800, color: C.blue }}>{m.total.toLocaleString('pt-BR')}</td>
                  <td style={{ ...S.td, textAlign: 'right', color: C.green }}>{m.atendidas.toLocaleString('pt-BR')}</td>
                  <td style={{ ...S.td, textAlign: 'right', color: C.red }}>{m.abandonadas.toLocaleString('pt-BR')}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{m.taxaAtend}%</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{m.taxaAbandono}%</td>
                  <td style={{ ...S.td, textAlign: 'right', color: C.orange }}>{secsToHMS(m.mediaEspera)}</td>
                  <td style={{ ...S.td, textAlign: 'right', color: C.teal }}>{secsToHMS(m.mediaFalada)}</td>
                  <td style={{ ...S.td, textAlign: 'right', color: C.purple }}>{secsToHMS(m.maiorTotal)}</td>
                  <td style={{ ...S.td, textAlign: 'right', color: C.purple }}>{secsToHMS(m.maiorFalada)}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{m.mediaLigacoesDia.toLocaleString('pt-BR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Componente principal ────────────────────────────────────────────────────────
export default function DashboardPlanilha() {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros — padrão: últimos 15 dias
  const [dataDe, setDataDe] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 15);
    return d.toISOString().split('T')[0];
  });
  const [dataAte, setDataAte] = useState(() => new Date().toISOString().split('T')[0]);
  const [statusSel, setStatusSel]   = useState('all');
  const [filaSel, setFilaSel]       = useState('all');
  const [catSel, setCatSel]         = useState('all');
  const [turnoSel, setTurnoSel]     = useState('all');
  const [agenteSel, setAgenteSel]   = useState('all');

  const [viewMode, setViewMode]     = useState('geral'); // 'geral' ou 'unidade'
  const [unidadeSel, setUnidadeSel] = useState('Homecare');
  const [datesInitialized, setDatesInitialized] = useState(true); // já iniciado acima

  const carregarUploads = useCallback(async () => {
    const q = query(collection(db, 'dashboard_uploads'), orderBy('timestamp', 'desc'));
    const snap = await getDocs(q);
    setUploads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }, []);
  const uploadsPolling = useBusinessHoursPolling(carregarUploads);

  // Achata todos os uploads em um array de rows (raw, sem enriquecimento)
  const allRows = useMemo(() => uploads.flatMap(u => u.rows || []), [uploads]);

  // Detecta campos dinamicamente — usa apenas as primeiras 5 linhas para performance
  const F = useMemo(() => {
    if (!allRows.length) return {};
    const keys = Object.keys(allRows[0]);
    return {
      data:      detectField(keys, ['data','date','datetime','datahora','data_hora']) || 'data',
      agente:    detectField(keys, ['agente','agent','atendente','operador','attendant']),
      fila:      detectField(keys, ['fila','queue','setor','destino','sector']),
      status:    detectField(keys, ['status','resultado','state','situacao']),
      espera:    detectField(keys, ['espera','wait','duracaoespera','duracao_espera','tempoespera','waittime']),
      falada:    detectField(keys, ['falada','talk','duracaofalada','duracao_falada','tempofalado','talktime']),
      total:     detectField(keys, ['total','duracao','duracaototal','duracao_total','duration']),
      origem:    detectField(keys, ['origem','origin','caller','telefone','numero','from']),
      categoria: detectField(keys, ['categoria','category','tipo','type']),
    };
  }, [allRows]);

  // PRÉ-FILTRA por data ANTES de enriquecer — evita processar 27k+ linhas de uma vez
  // Usa string comparison leve sem parseDateTime completo
  const preFilteredRows = useMemo(() => {
    if (!F.data || !dataDe) return allRows;
    const deTs  = dataDe  ? new Date(dataDe).getTime()                          : 0;
    const ateTs = dataAte ? new Date(dataAte + 'T23:59:59').getTime() : Infinity;
    return allRows.filter(r => {
      const raw = r[F.data];
      if (!raw) return false;
      const dt = parseDateTime(String(raw));
      if (!dt) return true; // não conseguiu parsear, inclui para não perder dados
      const ts = dt.getTime();
      return ts >= deTs && ts <= ateTs;
    });
  }, [allRows, F, dataDe, dataAte]);

  // Enriquece SOMENTE as linhas pré-filtradas (tipicamente 1k–3k, não 27k)
  const enriched = useMemo(() => preFilteredRows.map(r => {
    const dt = parseDateTime(r[F.data]);
    return {
      ...r,
      _dt:      dt,
      _hora:    dt ? dt.getHours() : null,
      _diaSem:  dt ? dt.getDay() : null,
      _dataStr: dt ? dt.toLocaleDateString('pt-BR') : '',
      _turno:   dt ? getTurno(dt.getHours()) : '',
      _espera:  toSeconds(r[F.espera]),
      _falada:  toSeconds(r[F.falada]),
      _total:   toSeconds(r[F.total]),
      _status:  r[F.status]    ? String(r[F.status]).trim()    : '',
      _agente:  r[F.agente]    ? String(r[F.agente]).trim()    : '',
      _fila:    r[F.fila]      ? String(r[F.fila]).trim()      : '',
      _cat:     r[F.categoria] ? String(r[F.categoria]).trim() : '',
      _origem:  classifyOrigem(r[F.origem]),
      _unidade: getUnidade(r[F.fila] ? String(r[F.fila]).trim() : ''),
    };
  }), [preFilteredRows, F]);

  // Datas mín/máx do dataset completo — usando reduce para evitar stack overflow com Math.min(...array grande)
  const defaultDates = useMemo(() => {
    if (!allRows.length || !F.data) return { min: '', max: '' };
    let minTs = Infinity, maxTs = -Infinity;
    for (const r of allRows) {
      const dt = parseDateTime(r[F.data]);
      if (!dt) continue;
      const ts = dt.getTime();
      if (ts < minTs) minTs = ts;
      if (ts > maxTs) maxTs = ts;
    }
    if (minTs === Infinity) return { min: '', max: '' };
    const toInputDate = d => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    return { min: toInputDate(new Date(minTs)), max: toInputDate(new Date(maxTs)) };
  }, [allRows, F]);

  // Opções únicas para filtros
  const optsUnidades = useMemo(() => [...new Set(enriched.map(r => r._unidade).filter(Boolean).filter(u => u !== 'Outros' && u !== 'Indeterminado'))].sort(), [enriched]);
  const optsCats    = useMemo(() => [...new Set(enriched.map(r => r._cat).filter(Boolean))].sort(), [enriched]);
  const optsAgentes = useMemo(() => [...new Set(enriched.map(r => r._agente).filter(Boolean).filter(a => a !== '---'))].sort(), [enriched]);
  const optsStatus  = useMemo(() => [...new Set(enriched.map(r => r._status).filter(Boolean))].sort(), [enriched]);

  // Filtragem
  const baseFiltradaUnidade = useMemo(() => {
    let rows = enriched;
    if (viewMode === 'unidade' || viewMode === 'mensal') rows = rows.filter(r => r._unidade === unidadeSel);
    return rows;
  }, [enriched, viewMode, unidadeSel]);

  const optsFilas   = useMemo(() => [...new Set(baseFiltradaUnidade.map(r => r._fila).filter(Boolean))].sort(), [baseFiltradaUnidade]);

  const filtered = useMemo(() => {
    let rows = baseFiltradaUnidade;
    // Nota: filtro de data já foi aplicado em preFilteredRows para performance
    if (statusSel !== 'all') rows = rows.filter(r => r._status === statusSel);
    if (filaSel   !== 'all') rows = rows.filter(r => r._fila   === filaSel);
    if (catSel    !== 'all') rows = rows.filter(r => r._cat    === catSel);
    if (turnoSel  !== 'all') rows = rows.filter(r => r._turno  === turnoSel);
    if (agenteSel !== 'all') rows = rows.filter(r => r._agente === agenteSel);
    return rows;
  }, [baseFiltradaUnidade, statusSel, filaSel, catSel, turnoSel, agenteSel]);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total = filtered.length;
    const atendidas    = filtered.filter(r => r._status === 'Atendida').length;
    const abandonadas  = filtered.filter(r => r._status === 'Abandonada').length;
    const taxaAtend    = total ? (atendidas / total * 100).toFixed(1) : '0.0';
    const taxaAbandono = total ? (abandonadas / total * 100).toFixed(1) : '0.0';
    const somaEspera   = filtered.reduce((a, r) => a + r._espera, 0);
    const somaFalada   = filtered.filter(r => r._status === 'Atendida').reduce((a, r) => a + r._falada, 0);
    const mediaEspera  = total ? Math.round(somaEspera / total) : 0;
    const mediaFalada  = atendidas ? Math.round(somaFalada / atendidas) : 0;
    const maiorTotal   = filtered.reduce((max, r) => Math.max(max, r._total), 0);
    const maiorFalada  = filtered.reduce((max, r) => Math.max(max, r._falada), 0);
    return { total, atendidas, abandonadas, taxaAtend, taxaAbandono, mediaEspera, mediaFalada, maiorTotal, maiorFalada };
  }, [filtered]);

  // ── Rank de agentes ────────────────────────────────────────────────────────
  const rankAgentes = useMemo(() => {
    const map = {};
    filtered.filter(r => r._agente && r._agente !== '---').forEach(r => {
      if (!map[r._agente]) map[r._agente] = { agente: r._agente, recebidas: 0, atendidas: 0, transferidas: 0, somaFalada: 0 };
      map[r._agente].recebidas++;
      if (r._status === 'Atendida')    { map[r._agente].atendidas++; map[r._agente].somaFalada += r._falada; }
      if (r._status === 'Transferida')   map[r._agente].transferidas++;
    });
    return Object.values(map)
      .map(a => ({ ...a, taxaAtend: a.recebidas ? (a.atendidas/a.recebidas*100).toFixed(1) : '0.0', mediaFalada: a.atendidas ? Math.round(a.somaFalada/a.atendidas) : 0 }))
      .sort((a, b) => b.atendidas - a.atendidas);
  }, [filtered]);

  // ── Faixas de tempo ────────────────────────────────────────────────────────
  function calcFaixas(rows, campo) {
    return FAIXAS_ESPERA.map(f => {
      const count = rows.filter(r => {
        const v = r[campo];
        if (f.max === 0) return v === 0;
        return v >= f.min && v <= f.max;
      }).length;
      return { label: f.label, count, pct: rows.length ? (count/rows.length*100).toFixed(1) : '0.0' };
    });
  }
  const faixasEspera = useMemo(() => calcFaixas(filtered, '_espera'), [filtered]);
  const faixasFalada = useMemo(() => calcFaixas(filtered.filter(r => r._status === 'Atendida'), '_falada'), [filtered]);

  // ── Por fila ───────────────────────────────────────────────────────────────
  const porFila = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const f = r._fila || 'N/D';
      if (!map[f]) map[f] = { fila: f, total: 0, somaEspera: 0, somaFalada: 0, atendidas: 0, abandonos: 0 };
      map[f].total++;
      map[f].somaEspera += r._espera;
      if (r._status === 'Atendida')   { map[f].atendidas++; map[f].somaFalada += r._falada; }
      if (r._status === 'Abandonada')   map[f].abandonos++;
    });
    return Object.values(map)
      .map(f => ({ ...f, mediaEspera: f.total ? Math.round(f.somaEspera/f.total) : 0, mediaFalada: f.atendidas ? Math.round(f.somaFalada/f.atendidas) : 0 }))
      .sort((a, b) => b.total - a.total);
  }, [filtered]);

  // ── Volume por dia ─────────────────────────────────────────────────────────
  const volumePorDia = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const d = r._dataStr;
      if (!d) return;
      if (!map[d]) map[d] = { data: d, Recebidas: 0, Atendidas: 0, Perdidas: 0, _dt: r._dt };
      map[d].Recebidas++;
      if (r._status === 'Atendida') map[d].Atendidas++;
      if (r._status === 'Abandonada') map[d].Perdidas++;
    });
    return Object.values(map).sort((a, b) => (a._dt||0) - (b._dt||0));
  }, [filtered]);

  // ── Análise Mês a Mês — mesma base "filtered" de todo o resto do dashboard,
  // só reorganizada por competência (ano-mês) pra comparar cada indicador ──────
  const MESES_PT = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  const porMesAno = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      if (!r._dt) return;
      const key = `${r._dt.getFullYear()}-${String(r._dt.getMonth() + 1).padStart(2, '0')}`;
      if (!map[key]) map[key] = {
        mesKey: key, ano: r._dt.getFullYear(), mesIdx: r._dt.getMonth(),
        total: 0, atendidas: 0, abandonadas: 0, transferidas: 0,
        somaEspera: 0, somaFalada: 0, maiorTotal: 0, maiorFalada: 0,
      };
      const m = map[key];
      m.total++;
      if (r._status === 'Atendida')   { m.atendidas++; m.somaFalada += r._falada; }
      if (r._status === 'Abandonada')   m.abandonadas++;
      if (r._status === 'Transferida')  m.transferidas++;
      m.somaEspera += r._espera;
      if (r._total  > m.maiorTotal)  m.maiorTotal  = r._total;
      if (r._falada > m.maiorFalada) m.maiorFalada = r._falada;
    });
    return Object.values(map)
      .map(m => ({
        ...m,
        mesLabel: `${MESES_PT[m.mesIdx]}/${String(m.ano).slice(2)}`,
        taxaAtend:    m.total ? +(m.atendidas / m.total * 100).toFixed(1)    : 0,
        taxaAbandono: m.total ? +(m.abandonadas / m.total * 100).toFixed(1) : 0,
        mediaEspera:  m.total     ? Math.round(m.somaEspera / m.total)      : 0,
        mediaFalada:  m.atendidas ? Math.round(m.somaFalada / m.atendidas)  : 0,
        // "Média de ligações por dia" no mês, considerando os dias distintos que tiveram alguma chamada
        mediaLigacoesDia: (() => {
          const dias = new Set(filtered.filter(r => r._dt && `${r._dt.getFullYear()}-${String(r._dt.getMonth()+1).padStart(2,'0')}` === m.mesKey).map(r => r._dataStr));
          return dias.size ? Math.round(m.total / dias.size) : 0;
        })(),
      }))
      .sort((a, b) => a.mesKey.localeCompare(b.mesKey));
  }, [filtered]); // eslint-disable-line react-hooks/exhaustive-deps

  // Quantitativo de atendimentos por fila, mês a mês (mesma base "filtered") —
  // pivô: uma linha por fila, uma coluna por mês
  const porMesFila = useMemo(() => {
    const mesesSet = new Set();
    const map = {};
    filtered.forEach(r => {
      if (!r._dt) return;
      const mesKey = `${r._dt.getFullYear()}-${String(r._dt.getMonth() + 1).padStart(2, '0')}`;
      const fila = r._fila || 'N/D';
      mesesSet.add(mesKey);
      if (!map[fila]) map[fila] = { fila, total: 0 };
      map[fila][mesKey] = (map[fila][mesKey] || 0) + 1;
      map[fila].total++;
    });
    const meses = [...mesesSet].sort().map(k => {
      const [ano, mes] = k.split('-');
      return { mesKey: k, mesLabel: `${MESES_PT[Number(mes) - 1]}/${String(ano).slice(2)}` };
    });
    const linhas = Object.values(map).sort((a, b) => b.total - a.total);
    return { linhas, meses };
  }, [filtered]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Volume por hora ────────────────────────────────────────────────────────
  const volumePorHora = useMemo(() => {
    const arr = Array.from({ length: 24 }, (_, h) => ({ hora: `${String(h).padStart(2,'0')}h`, total: 0, atendidas: 0 }));
    filtered.forEach(r => {
      if (r._hora == null) return;
      arr[r._hora].total++;
      if (r._status === 'Atendida') arr[r._hora].atendidas++;
    });
    return arr;
  }, [filtered]);

  // ── Heatmap hora × dia da semana ──────────────────────────────────────────
  const heatmapData = useMemo(() => {
    const matrix = {};
    let maxVal = 0;
    filtered.forEach(r => {
      if (r._hora == null || r._diaSem == null) return;
      const k = `${r._diaSem}_${r._hora}`;
      matrix[k] = (matrix[k] || 0) + 1;
      if (matrix[k] > maxVal) maxVal = matrix[k];
    });
    return { matrix, maxVal };
  }, [filtered]);

  // ── Origem das chamadas ────────────────────────────────────────────────────
  const origemData = useMemo(() => {
    const map = {};
    filtered.forEach(r => { map[r._origem] = (map[r._origem] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filtered]);

  const filtrosAtivos = [statusSel, filaSel, catSel, turnoSel, agenteSel].filter(v => v !== 'all').length + (dataDe && dataDe !== defaultDates.min ? 1 : 0) + (dataAte && dataAte !== defaultDates.max ? 1 : 0);

  const limparFiltros = () => {
    if (defaultDates.min && defaultDates.max) {
      setDataDe(defaultDates.min);
      setDataAte(defaultDates.max);
    } else {
      setDataDe('');
      setDataAte('');
    }
    setStatusSel('all'); setFilaSel('all'); setCatSel('all'); setTurnoSel('all'); setAgenteSel('all'); 
  };

  if (loading) return (
    <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>📞</div>
        <div style={{ color: C.blue, fontWeight: 700 }}>Carregando...</div>
      </div>
    </div>
  );

  if (!uploads.length || allRows.length === 0) return (
    <div style={S.page}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
        <div style={{ width: 48, height: 48, background: `linear-gradient(135deg,${C.blue},${C.purple})`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', boxShadow: `0 4px 16px ${C.blue}40` }}>📞</div>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800 }}>Dashboard de Telefonia</h1>
          <p style={{ margin: '3px 0 0', fontSize: '0.83rem', color: C.muted }}>Indicadores e análises das chamadas</p>
        </div>
      </div>
      <div style={{ ...S.card, textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: '3.5rem', marginBottom: 14 }}>📭</div>
        <div style={{ fontWeight: 700, color: C.muted, marginBottom: 8 }}>Nenhum dado disponível</div>
        <div style={{ color: C.dim, fontSize: '0.85rem' }}>Acesse "Upload de Planilha" e envie um arquivo XML ou XLSX de telefonia.</div>
        {uploads.length > 0 && (
          <div style={{ marginTop: 20, fontSize: '0.8rem', color: C.orange }}>
            ⚠️ {uploads.length} upload(s) encontrado(s), mas sem linhas legíveis. Verifique o formato do arquivo.
          </div>
        )}
        <button onClick={uploadsPolling.refresh} disabled={uploadsPolling.refreshing} style={{ ...S.chip(false, C.blue), marginTop: 16 }}>
          {uploadsPolling.refreshing ? '⏳ Atualizando...' : '🔄 Atualizar'}
        </button>
      </div>
    </div>
  );

  return (
    <div style={S.page}>

      {/* ── CABEÇALHO ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, background: `linear-gradient(135deg,${C.blue},${C.purple})`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', boxShadow: `0 4px 16px ${C.blue}40` }}>📞</div>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800 }}>Dashboard de Telefonia</h1>
            <p style={{ margin: '3px 0 0', fontSize: '0.83rem', color: C.muted }}>
              <strong style={{ color: C.text }}>{filtered.length.toLocaleString('pt-BR')}</strong> chamadas
              {filtrosAtivos > 0 && <span style={{ color: C.orange }}> · {filtrosAtivos} filtro(s) ativo(s)</span>}
            </p>
          </div>
        </div>
        
        {/* Abas */}
        <div style={{ display: 'flex', gap: 8, background: 'rgba(255,255,255,0.05)', padding: 6, borderRadius: 12 }}>
          <button style={S.chip(viewMode === 'geral', C.blue)} onClick={() => setViewMode('geral')}>🌐 Visão Geral</button>
          <button style={S.chip(viewMode === 'unidade', C.purple)} onClick={() => setViewMode('unidade')}>🏢 Visão por Unidade</button>
          <button style={S.chip(viewMode === 'mensal', C.yellow)} onClick={() => setViewMode('mensal')}>📅 Análise Mês a Mês</button>
        </div>

        {filtrosAtivos > 0 && (
          <button onClick={limparFiltros} style={{ ...S.chip(true, C.red), padding: '7px 16px' }}>✕ Limpar filtros</button>
        )}
        <button onClick={uploadsPolling.refresh} disabled={uploadsPolling.refreshing}
          title="Atualiza sozinho de hora em hora em horário comercial — clique para forçar agora"
          style={{ ...S.chip(false, C.blue), display: 'flex', alignItems: 'center', gap: 6 }}>
          {uploadsPolling.refreshing ? '⏳' : '🔄'} {uploadsPolling.lastUpdated ? `Atualizado ${formatLastUpdated(uploadsPolling.lastUpdated)}` : 'Atualizar'}
        </button>
      </div>

      {/* ── BARRA DE FILTROS ──────────────────────────────────────────────── */}
      <div style={{ ...S.card, marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', padding: '14px 20px' }}>

        {(viewMode === 'unidade' || viewMode === 'mensal') && optsUnidades.length > 0 && (
          <div style={{ borderRight: `1px solid ${C.border}`, paddingRight: 16, marginRight: 4 }}>
            <label style={{ display: 'block', fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.purple, marginBottom: 3 }}>🏢 Unidade</label>
            <select style={{ ...S.sel, borderColor: C.purple, background: `${C.purple}20`, fontWeight: 700 }} value={unidadeSel} onChange={e => setUnidadeSel(e.target.value)}>
              {optsUnidades.map(v => <option key={v} value={v} style={{ background: '#1e293b', color: '#f1f5f9' }}>{v}</option>)}
            </select>
          </div>
        )}

        <div>
          <label style={{ display: 'block', fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, marginBottom: 3 }}>📅 De</label>
          <input type="date" style={S.sel} value={dataDe} onChange={e => setDataDe(e.target.value)} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, marginBottom: 3 }}>Até</label>
          <input type="date" style={S.sel} value={dataAte} onChange={e => setDataAte(e.target.value)} />
        </div>

        {/* Atalhos de período */}
        <div>
          <label style={{ display: 'block', fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, marginBottom: 3 }}>⚡ Período</label>
          <div style={{ display: 'flex', gap: 4 }}>
            {[
              { label: 'Hoje',  days: 0 },
              { label: '7d',    days: 7 },
              { label: '15d',   days: 15 },
              { label: '30d',   days: 30 },
              { label: 'Tudo',  days: null },
            ].map(({ label, days }) => (
              <button
                key={label}
                style={S.chip(false, C.blue)}
                onClick={() => {
                  const hoje = new Date().toISOString().split('T')[0];
                  setDataAte(hoje);
                  if (days === null) {
                    setDataDe(defaultDates.min || hoje);
                  } else {
                    const d = new Date();
                    d.setDate(d.getDate() - days);
                    setDataDe(d.toISOString().split('T')[0]);
                  }
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {optsStatus.length > 0 && (
          <div>
            <label style={{ display: 'block', fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, marginBottom: 3 }}>🔵 Status</label>
            <div style={{ display: 'flex', gap: 4 }}>
              <button style={S.chip(statusSel === 'all', C.blue)} onClick={() => setStatusSel('all')}>Todos</button>
              {optsStatus.map(v => <button key={v} style={S.chip(statusSel === v, STATUS_COLORS[v] || C.blue)} onClick={() => setStatusSel(v)}>{v}</button>)}
            </div>
          </div>
        )}

        {optsFilas.length > 0 && (
          <div>
            <label style={{ display: 'block', fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, marginBottom: 3 }}>🏢 Fila/Setor</label>
            <select style={S.sel} value={filaSel} onChange={e => setFilaSel(e.target.value)}>
              <option value="all" style={{ background: '#1e293b', color: '#f1f5f9' }}>Todas</option>
              {optsFilas.map(v => <option key={v} value={v} style={{ background: '#1e293b', color: '#f1f5f9' }}>{v}</option>)}
            </select>
          </div>
        )}

        {optsCats.length > 0 && (
          <div>
            <label style={{ display: 'block', fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, marginBottom: 3 }}>📱 Categoria</label>
            <select style={S.sel} value={catSel} onChange={e => setCatSel(e.target.value)}>
              <option value="all" style={{ background: '#1e293b', color: '#f1f5f9' }}>Todas</option>
              {optsCats.map(v => <option key={v} value={v} style={{ background: '#1e293b', color: '#f1f5f9' }}>{v}</option>)}
            </select>
          </div>
        )}

        <div>
          <label style={{ display: 'block', fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, marginBottom: 3 }}>🌙 Turno</label>
          <div style={{ display: 'flex', gap: 4 }}>
            {[['all','Todos'],['Diurno','☀️ Diurno'],['Noturno','🌙 Noturno']].map(([v,l]) => (
              <button key={v} style={S.chip(turnoSel === v, C.purple)} onClick={() => setTurnoSel(v)}>{l}</button>
            ))}
          </div>
        </div>

        {optsAgentes.length > 0 && (
          <div>
            <label style={{ display: 'block', fontSize: '0.63rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, marginBottom: 3 }}>👤 Agente</label>
            <select style={S.sel} value={agenteSel} onChange={e => setAgenteSel(e.target.value)}>
              <option value="all" style={{ background: '#1e293b', color: '#f1f5f9' }}>Todos</option>
              {optsAgentes.map(v => <option key={v} value={v} style={{ background: '#1e293b', color: '#f1f5f9' }}>{v}</option>)}
            </select>
          </div>
        )}
      </div>

      {viewMode === 'mensal' ? (
        <MonthlyComparisonSection porMesAno={porMesAno} porMesFila={porMesFila} />
      ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* ── LINHA 1: KPI CARDS ────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))', gap: 14 }}>
          <KpiCard icon="📞" label="Total de Chamadas"     value={kpis.total.toLocaleString('pt-BR')}   color={C.blue} />
          <KpiCard icon="⏱️" label="Tempo Médio de Espera" value={secsToHMS(kpis.mediaEspera)}          color={C.orange} />
          <KpiCard icon="💬" label="Tempo Médio Falado"    value={secsToHMS(kpis.mediaFalada)}          color={C.teal} />
          <KpiCard icon="📏" label="Maior Duração em Linha" value={secsToHMS(kpis.maiorTotal)}          color={C.purple} />
          <KpiCard icon="🎙️" label="Maior Tempo Falado"    value={secsToHMS(kpis.maiorFalada)}          color={C.purple} />
          <KpiCard icon="✅" label="Taxa de Atendimento"   value={`${kpis.taxaAtend}%`}                 color={C.green}
            sub={`${kpis.atendidas.toLocaleString('pt-BR')} atendidas`} />
          <KpiCard icon="❌" label="Taxa de Abandono"      value={`${kpis.taxaAbandono}%`}              color={C.red}
            warn={parseFloat(kpis.taxaAbandono) > 10}
            sub={`${kpis.abandonadas.toLocaleString('pt-BR')} abandonadas`} />
        </div>

        {/* ── SEÇÃO FOCO DA UNIDADE (Apenas se viewMode === 'unidade') ──── */}
        {viewMode === 'unidade' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
            <div style={{ ...S.card, borderTop: `3px solid ${C.red}` }}>
              <SectionTitle color={C.red}>❌ Abandonos por Fila ({unidadeSel})</SectionTitle>
              <div style={{ overflowX: 'auto', maxHeight: 220 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr><th style={S.th}>Fila</th><th style={{...S.th, textAlign:'right'}}>Abandonos</th><th style={{...S.th, textAlign:'right'}}>% do Perdido</th></tr>
                  </thead>
                  <tbody>
                    {porFila.filter(f => f.abandonos > 0).sort((a,b)=>b.abandonos - a.abandonos).map(f => (
                      <tr key={f.fila}>
                        <td style={S.td}>{f.fila}</td>
                        <td style={{...S.td, textAlign:'right', color: C.red, fontWeight: 700}}>{f.abandonos}</td>
                        <td style={{...S.td, textAlign:'right', color: C.muted}}>{kpis.abandonadas ? ((f.abandonos/kpis.abandonadas)*100).toFixed(1) : 0}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ ...S.card, borderTop: `3px solid ${C.orange}` }}>
              <SectionTitle color={C.orange}>⏱️ Maiores Tempos de Espera ({unidadeSel})</SectionTitle>
              <div style={{ overflowX: 'auto', maxHeight: 220 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr><th style={S.th}>Fila</th><th style={{...S.th, textAlign:'right'}}>Maior Espera</th><th style={{...S.th, textAlign:'right'}}>Méd. Espera</th></tr>
                  </thead>
                  <tbody>
                    {porFila.map(f => {
                      const maxEsperaFila = filtered.filter(r => r._fila === f.fila).reduce((max, r) => Math.max(max, r._espera), 0);
                      return { ...f, maxEspera: maxEsperaFila };
                    }).sort((a,b)=>b.maxEspera - a.maxEspera).slice(0, 10).map(f => (
                      <tr key={f.fila}>
                        <td style={S.td}>{f.fila}</td>
                        <td style={{...S.td, textAlign:'right', color: C.orange, fontWeight: 700}}>{secsToHMS(f.maxEspera)}</td>
                        <td style={{...S.td, textAlign:'right', color: C.muted}}>{secsToHMS(f.mediaEspera)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ ...S.card, borderTop: `3px solid ${C.teal}` }}>
              <SectionTitle color={C.teal}>💬 Maiores Tempos Falados ({unidadeSel})</SectionTitle>
              <div style={{ overflowX: 'auto', maxHeight: 220 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr><th style={S.th}>Fila</th><th style={{...S.th, textAlign:'right'}}>Maior Ligação</th><th style={{...S.th, textAlign:'right'}}>Méd. Ligação</th></tr>
                  </thead>
                  <tbody>
                    {porFila.map(f => {
                      const maxFaladaFila = filtered.filter(r => r._fila === f.fila && r._status === 'Atendida').reduce((max, r) => Math.max(max, r._falada), 0);
                      return { ...f, maxFalada: maxFaladaFila };
                    }).sort((a,b)=>b.maxFalada - a.maxFalada).slice(0, 10).map(f => (
                      <tr key={f.fila}>
                        <td style={S.td}>{f.fila}</td>
                        <td style={{...S.td, textAlign:'right', color: C.teal, fontWeight: 700}}>{secsToHMS(f.maxFalada)}</td>
                        <td style={{...S.td, textAlign:'right', color: C.muted}}>{secsToHMS(f.mediaFalada)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── LINHA 2: VOLUME POR DIA + HORÁRIOS DE PICO ──────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 16 }}>

          {/* Volume por dia */}
          <div style={S.card}>
            <SectionTitle>📅 Volume por Dia</SectionTitle>
            {volumePorDia.length === 0
              ? <div style={{ color: C.dim, textAlign: 'center', padding: '30px 0' }}>Sem dados para o período</div>
              : <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={volumePorDia} barCategoryGap="25%" barGap={2} margin={{ top: 5, right: 10, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="data" tick={{ fill: C.dim, fontSize: 10 }} angle={-25} textAnchor="end" interval={0} />
                    <YAxis tick={{ fill: C.dim, fontSize: 10 }} />
                    <Tooltip content={<Tip />} />
                    <Legend wrapperStyle={{ fontSize: '0.78rem', color: C.muted }} />
                    <Bar dataKey="Recebidas" fill={C.blue}   radius={[4,4,0,0]} maxBarSize={18}>
                      <LabelList dataKey="Recebidas" position="top" fill={C.muted} fontSize={9} />
                    </Bar>
                    <Bar dataKey="Atendidas" fill={C.green}  radius={[4,4,0,0]} maxBarSize={18}>
                      <LabelList dataKey="Atendidas" position="top" fill={C.muted} fontSize={9} />
                    </Bar>
                    <Bar dataKey="Perdidas"  fill={C.red}    radius={[4,4,0,0]} maxBarSize={18}>
                      <LabelList dataKey="Perdidas" position="top" fill={C.muted} fontSize={9} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
            }
          </div>

          {/* Horários de Pico */}
          <div style={S.card}>
            <SectionTitle>🕐 Horários de Pico</SectionTitle>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={volumePorHora} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="hora" tick={{ fill: C.dim, fontSize: 9 }} interval={1} />
                <YAxis tick={{ fill: C.dim, fontSize: 9 }} />
                <Tooltip content={<Tip />} />
                <Bar dataKey="total" name="Chamadas" fill={C.blue} radius={[3,3,0,0]}>
                  {volumePorHora.map((entry, i) => {
                    const pct = kpis.total ? entry.total / kpis.total : 0;
                    const color = pct > 0.06 ? C.red : pct > 0.04 ? C.orange : C.blue;
                    return <Cell key={i} fill={color} />;
                  })}
                  <LabelList dataKey="total" position="top" fill={C.muted} fontSize={9} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── LINHA 3: HEATMAP + ORIGEM ────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 16 }}>

          {/* Heatmap Hora × Dia da Semana */}
          <div style={S.card}>
            <SectionTitle>🗓️ Mapa de Calor — Hora × Dia da Semana</SectionTitle>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: 2, fontSize: '0.7rem', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ ...S.th, minWidth: 30, width: 30 }}></th>
                    {Array.from({ length: 24 }, (_, h) => (
                      <th key={h} style={{ ...S.th, textAlign: 'center', minWidth: 26, padding: '4px 2px' }}>{h}h</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DIAS_SEMANA.map((dia, d) => (
                    <tr key={d}>
                      <td style={{ ...S.td, fontWeight: 700, color: C.muted, whiteSpace: 'nowrap', padding: '3px 6px' }}>{dia}</td>
                      {Array.from({ length: 24 }, (_, h) => {
                        const val = heatmapData.matrix[`${d}_${h}`] || 0;
                        const intensity = heatmapData.maxVal > 0 ? val / heatmapData.maxVal : 0;
                        const bg = val > 0 ? `rgba(99,102,241,${0.1 + intensity * 0.85})` : 'rgba(255,255,255,0.02)';
                        return (
                          <td key={h} title={`${dia} ${h}h — ${val} chamadas`} style={{ textAlign: 'center', background: bg, color: intensity > 0.6 ? '#fff' : C.dim, fontWeight: val > 0 ? 700 : 400, borderRadius: 4, padding: '3px 2px', cursor: val > 0 ? 'default' : 'default', fontSize: '0.65rem' }}>
                            {val || ''}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Origem das Chamadas */}
          <div style={S.card}>
            <SectionTitle>📱 Origem das Chamadas</SectionTitle>
            {origemData.length === 0
              ? <div style={{ color: C.dim, textAlign: 'center', padding: '30px 0' }}>Sem dados de origem</div>
              : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                  <ResponsiveContainer width={180} height={180}>
                    <PieChart>
                      <Pie data={origemData} cx="50%" cy="50%" innerRadius={45} outerRadius={80}
                        dataKey="value" nameKey="name"
                        label={({ percent }) => percent > 0.05 ? `${(percent*100).toFixed(0)}%` : ''}
                        labelLine={false}>
                        {origemData.map((_, i) => <Cell key={i} fill={CHART_CC[i % CHART_CC.length]} />)}
                      </Pie>
                      <Tooltip content={<Tip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {origemData.map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: CHART_CC[i % CHART_CC.length] }} />
                          <span style={{ fontSize: '0.8rem' }}>{item.name}</span>
                        </div>
                        <span style={{ fontWeight: 800, color: CHART_CC[i % CHART_CC.length] }}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* ── LINHA 4: FAIXAS DE ESPERA + FAIXAS FALADO ───────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

          {/* Faixas de Espera */}
          <div style={S.card}>
            <SectionTitle color={C.orange}>⏱️ Faixas — Tempo de Espera na Fila</SectionTitle>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={S.th}>Faixa</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Qtd</th>
                  <th style={{ ...S.th, minWidth: 130 }}>% do Total</th>
                </tr>
              </thead>
              <tbody>
                {faixasEspera.map((f, i) => (
                  <tr key={i}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{f.label}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 800, color: C.orange }}>{f.count.toLocaleString('pt-BR')}</td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${f.pct}%`, background: C.orange, borderRadius: 999 }} />
                        </div>
                        <span style={{ fontSize: '0.72rem', color: C.muted, minWidth: 36, textAlign: 'right' }}>{f.pct}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Faixas de Falado */}
          <div style={S.card}>
            <SectionTitle color={C.teal}>💬 Faixas — Tempo Falado (atendidas)</SectionTitle>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={S.th}>Faixa</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Qtd</th>
                  <th style={{ ...S.th, minWidth: 130 }}>% do Total</th>
                </tr>
              </thead>
              <tbody>
                {faixasFalada.map((f, i) => (
                  <tr key={i}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{f.label}</td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 800, color: C.teal }}>{f.count.toLocaleString('pt-BR')}</td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${f.pct}%`, background: C.teal, borderRadius: 999 }} />
                        </div>
                        <span style={{ fontSize: '0.72rem', color: C.muted, minWidth: 36, textAlign: 'right' }}>{f.pct}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── LINHA 5: CHAMADAS POR FILA ───────────────────────────────── */}
        <div style={S.card}>
          <SectionTitle>🏢 Chamadas por Fila / Setor</SectionTitle>
          {porFila.length === 0
            ? <div style={{ color: C.dim, textAlign: 'center', padding: '20px 0' }}>Coluna "Fila" não detectada nos dados</div>
            : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={S.th}>Fila / Setor</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Total</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Atendidas</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Abandonos</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Méd. Espera</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Méd. Falado</th>
                      <th style={{ ...S.th, minWidth: 100 }}>Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porFila.map((row, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                        <td style={{ ...S.td, fontWeight: 700, color: C.text }}>{row.fila}</td>
                        <td style={{ ...S.td, textAlign: 'right', fontWeight: 800, color: C.blue }}>{row.total.toLocaleString('pt-BR')}</td>
                        <td style={{ ...S.td, textAlign: 'right', color: C.green }}>{row.atendidas}</td>
                        <td style={{ ...S.td, textAlign: 'right', color: C.red }}>{row.abandonos}</td>
                        <td style={{ ...S.td, textAlign: 'right', color: C.orange }}>{secsToHMS(row.mediaEspera)}</td>
                        <td style={{ ...S.td, textAlign: 'right', color: C.teal }}>{secsToHMS(row.mediaFalada)}</td>
                        <td style={S.td}>
                          <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 999, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${porFila[0]?.total ? (row.total/porFila[0].total)*100 : 0}%`, background: CHART_CC[i % CHART_CC.length], borderRadius: 999 }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>

        {/* ── LINHA 6: RANK DE AGENTES ─────────────────────────────────── */}
        <div style={S.card}>
          <SectionTitle color={C.yellow}>🏆 Ranking de Agentes</SectionTitle>
          {rankAgentes.length === 0
            ? <div style={{ color: C.dim, textAlign: 'center', padding: '20px 0' }}>Sem agentes no período filtrado</div>
            : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={S.th}>#</th>
                      <th style={S.th}>Agente</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Recebidas</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Atendidas</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Transferidas</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Taxa Atend.</th>
                      <th style={{ ...S.th, textAlign: 'right' }}>Méd. Falado</th>
                      <th style={{ ...S.th, minWidth: 100 }}>Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankAgentes.map((a, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                        <td style={{ ...S.td, fontWeight: 800, color: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : i === 2 ? '#b45309' : C.dim }}>
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i+1}`}
                        </td>
                        <td style={{ ...S.td, fontWeight: 700, color: C.text }}>{a.agente}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{a.recebidas}</td>
                        <td style={{ ...S.td, textAlign: 'right', color: C.green, fontWeight: 700 }}>{a.atendidas}</td>
                        <td style={{ ...S.td, textAlign: 'right', color: C.teal }}>{a.transferidas || '—'}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>
                          <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, background: parseFloat(a.taxaAtend) >= 80 ? `${C.green}20` : parseFloat(a.taxaAtend) >= 50 ? `${C.orange}20` : `${C.red}20`, color: parseFloat(a.taxaAtend) >= 80 ? C.green : parseFloat(a.taxaAtend) >= 50 ? C.orange : C.red }}>
                            {a.taxaAtend}%
                          </span>
                        </td>
                        <td style={{ ...S.td, textAlign: 'right', color: C.teal }}>{secsToHMS(a.mediaFalada)}</td>
                        <td style={S.td}>
                          <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 999, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${rankAgentes[0]?.atendidas ? (a.atendidas/rankAgentes[0].atendidas)*100 : 0}%`, background: C.blue, borderRadius: 999 }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>

      </div>
      )}
    </div>
  );
}
